import React from 'react';
import styled from '@emotion/styled';
import { Box, Typography, TypographyProps, Grid, css } from '@mui/material';
import Stack from '@mui/material/Grid';
import { colors } from 'theme/colors';

const StyledTypography = styled(Typography)`
  background-color: ${colors.primary900};
  color: ${colors.primary15};
  font-size: 20px;
  font-weight: 600;
`;

const StyledContainer = styled(Box)`
  height: 48px;
  min-width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  margin-bottom: 16px;
  background-color: ${colors.primary900};
  border: 1px solid ${colors.primary900};
  border-radius: 4px;
`;

const DetailsContainer = styled(Grid)`
  padding: 10px 20px 20px 20px;
  background-color: ${colors.gray100};
  border: 1px solid ${colors.gray200};
  width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
  border-radius: 4px;

  /* Override MUI Grid spacing margins that cause overflow */
  & > .MuiGrid-item {
    margin: 0;
    padding-left: 8px;
    padding-top: 8px;
  }
`;

const DetailsContainerWhite = styled(Grid)`
  padding: 0px;
  width: 100%;
  box-sizing: border-box;
  margin: 0 !important;
  border-radius: 4px;

  /* Override MUI Grid spacing margins that cause overflow */
  & > .MuiGrid-item {
    margin: 0;
    padding-left: 8px;
    padding-top: 8px;
  }
`;

const StyledDivider = styled(Box)`
  width: 100%;
  height: 1px;
  background-color: ${colors.gray300};
  margin: 0 0 16px 0;
`;

type TypographyWithChildrenProps = TypographyProps & {
  children?: React.ReactNode;
};

const SubsectionHeader = ({ children, ...typeProps }: TypographyWithChildrenProps) => (
  <StyledContainer>
    <StyledTypography {...typeProps}>{children}</StyledTypography>
  </StyledContainer>
);

const StyledPhoto = styled(Stack)`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.primary400};
  height: 320px;
  width: 270px;
`;

const StyledHeaderContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;

  @media (min-width: 1200px) {
    gap: 8px;
  }
`;

const StyledButtonsContainer = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;

  @media (min-width: 750px) {
    flex-direction: row;
    width: auto;
    flex: 1;
    min-width: max-content;
  }
`;

const StyledLink = styled(Typography)`
  text-decoration: none;
  color: ${colors.primary500};
  font-size: 0.875rem;
  line-height: 1.25rem;
  letter-spacing: 0%;
  cursor: pointer;
  color: ${colors.primary500} margin 0;
`;

interface IStyledOwnerDetail {
  warning: boolean;
  label?: boolean;
}

const StyledOwnerDetail = styled(Typography)<IStyledOwnerDetail>`
  ${(props: IStyledOwnerDetail) =>
    props.warning &&
    css`
      color: ${colors.warning800};
      p {
        color: ${colors.warning800};
      }
    `}
  ${(props: IStyledOwnerDetail) =>
    props.warning &&
    props.label &&
    css`
      &::after {
        content: '';
        display: inline-block;
        width: 16px;
        height: 16px;
        margin-left: 4px;
        background-image: url("data:image/svg+xml;utf8,<svg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M8.00016 14.6665C4.31816 14.6665 1.3335 11.6819 1.3335 7.99986C1.3335 4.31786 4.31816 1.33319 8.00016 1.33319C11.6822 1.33319 14.6668 4.31786 14.6668 7.99986C14.6668 11.6819 11.6822 14.6665 8.00016 14.6665ZM8.00016 13.3332C9.41465 13.3332 10.7712 12.7713 11.7714 11.7711C12.7716 10.7709 13.3335 9.41434 13.3335 7.99986C13.3335 6.58537 12.7716 5.22882 11.7714 4.22862C10.7712 3.22843 9.41465 2.66652 8.00016 2.66652C6.58567 2.66652 5.22912 3.22843 4.22893 4.22862C3.22873 5.22882 2.66683 6.58537 2.66683 7.99986C2.66683 9.41434 3.22873 10.7709 4.22893 11.7711C5.22912 12.7713 6.58567 13.3332 8.00016 13.3332ZM7.3335 9.99986H8.66683V11.3332H7.3335V9.99986ZM7.3335 4.66652H8.66683V8.66652H7.3335V4.66652Z' fill='%23C1850E'/></svg>");
        background-repeat: no-repeat;
        background-size: contain;
        vertical-align: middle;
      }
    `}
`;

export {
  SubsectionHeader,
  DetailsContainer,
  DetailsContainerWhite,
  StyledDivider,
  StyledPhoto,
  StyledHeaderContainer,
  StyledButtonsContainer,
  StyledLink,
  StyledOwnerDetail,
};
